import React, { useEffect, useState, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Coerce any API error detail (string | FastAPI 422 array | object) into a safe string.
// Never return an object — React cannot render objects as children.
const _errText = (d) => {
  if (!d) return '';
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((e) => (e && e.msg) || (typeof e === 'string' ? e : JSON.stringify(e))).join(', ');
  return d.msg || d.detail || JSON.stringify(d);
};

const DemoModeContext = React.createContext({
  state: null,
  refresh: () => {},
  openActivateModal: () => {},
  closeActivateModal: () => {},
  activateModalOpen: false,
  blockPayload: null,
  setBlockPayload: () => {},
});

export function useDemoMode() {
  return React.useContext(DemoModeContext);
}

export function DemoModeProvider({ user, token, children }) {
  const [state, setState] = useState(null);
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [blockPayload, setBlockPayload] = useState(null);

  const refresh = useCallback(async () => {
    if (!token) { setState(null); return; }
    try {
      const res = await fetch(`${API_URL}/api/demo-mode/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      const known = ['trial', 'readonly', 'suspended'];
      const next = data?.is_demo || known.includes(data?.account_state) ? data : { is_demo: false };
      setState(next);
      // Auto-open activate modal when ?activate=1 is present (drip /p, /payment redirects).
      // Done here (a callback, post-await) rather than in an effect.
      if (next.is_demo) {
        try {
          const params = new URLSearchParams(window.location.search);
          if (params.get('activate') === '1') {
            params.delete('activate');
            const cleaned = params.toString();
            const newUrl = window.location.pathname + (cleaned ? '?' + cleaned : '');
            window.history.replaceState({}, '', newUrl);
            window.dispatchEvent(new Event('demo:open-activate'));
          }
        } catch { /* */ }
      }
    } catch {
      setState({ is_demo: false });
    }
  }, [token]);

  useEffect(() => {
    const id = setTimeout(() => { refresh(); }, 0);
    return () => clearTimeout(id);
  }, [refresh]);

  // Handlers defined as stable callbacks (not inside the effect) so the
  // effect body contains no setState calls.
  const blockHandler = useCallback((e) => {
    const detail = e?.detail || {};
    if (detail?.demo_restricted) {
      setBlockPayload(detail);
      setActivateModalOpen(true);
    }
  }, []);
  const openHandler = useCallback(() => setActivateModalOpen(true), []);

  // Listen globally for 402 responses fired by the app's API helper, and for
  // the in-app open-activate event dispatched from refresh().
  useEffect(() => {
    window.addEventListener('demo:write-blocked', blockHandler);
    window.addEventListener('demo:open-activate', openHandler);
    return () => {
      window.removeEventListener('demo:write-blocked', blockHandler);
      window.removeEventListener('demo:open-activate', openHandler);
    };
  }, [blockHandler, openHandler]);

  const openActivateModal = useCallback((payload = null) => {
    if (payload) setBlockPayload(payload);
    setActivateModalOpen(true);
  }, []);
  const closeActivateModal = useCallback(() => { setActivateModalOpen(false); setBlockPayload(null); }, []);

  const value = {
    state,
    refresh,
    openActivateModal,
    closeActivateModal,
    activateModalOpen,
    blockPayload,
    setBlockPayload,
    user,
  };
  return <DemoModeContext.Provider value={value}>{children}</DemoModeContext.Provider>;
}

// ---------------- Sticky Top Banner ----------------

export function DemoModeBanner() {
  const { state, openActivateModal } = useDemoMode();
  const { language } = useLanguage();
  const known = ['trial', 'readonly', 'suspended'];
  if (!state || (!state.is_demo && !known.includes(state.account_state))) return null;
  const t = (hi, en) => (language === 'hi' ? hi : en);
  const days = state.days_left;
  const expired = state.expired;

  // --- Read-only / Suspended (post-trial locked) ---
  if (state.account_state === 'readonly' || state.account_state === 'suspended') {
    const suspended = state.account_state === 'suspended';
    const color = suspended ? 'from-rose-700 to-red-600' : 'from-amber-600 to-orange-500';
    const msg = suspended
      ? t('🔴 आपका account suspend है — pay करते ही तुरंत चालू। आपका data सुरक्षित है।',
          '🔴 Your account is suspended — pay to reactivate instantly. Your data is safe.')
      : t('🔶 Free Trial खत्म — अभी editing बंद है (देख/export कर सकते हैं)। जारी रखने के लिए subscribe करें।',
          '🔶 Free trial ended — editing is locked (you can still view/export). Subscribe to continue.');
    return (
      <div className={`bg-gradient-to-r ${color} text-white shadow-md`} data-testid="locked-mode-banner">
        <div className="px-3 sm:px-4 py-2 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm font-semibold flex-1 min-w-0">{msg}</p>
          <a href={`/pay/${state.school_id || 'default'}`} className="px-3 py-1.5 bg-white/95 hover:bg-white text-slate-900 rounded-full font-bold text-xs sm:text-sm whitespace-nowrap shadow-sm" data-testid="locked-banner-subscribe-btn">
            💳 {t('अभी Subscribe करें', 'Subscribe Now')}
          </a>
        </div>
      </div>
    );
  }

  // --- Trial accounts: informational countdown (already activated) ---
  if (state.account_state === 'trial') {
    const color = expired ? 'from-rose-600 to-red-500'
      : (days != null && days <= 3) ? 'from-amber-600 to-orange-500'
      : 'from-emerald-600 to-teal-500';
    const msg = expired
      ? t('आपका Free Trial खत्म हो गया है — subscription चालू रखने के लिए जल्द pay करें।',
          'Your free trial has ended — pay soon to keep your subscription active.')
      : t(`✅ Free Trial चालू है${days != null ? ` · ${days} दिन बचे` : ''} · Software FREE — one-time + सालाना maintenance (कुल users slab) पर subscribe करें।`,
          `✅ Free Trial active${days != null ? ` · ${days} day(s) left` : ''} · Software FREE — subscribe on a one-time + yearly maintenance (total-users slab).`);
    return (
      <div className={`bg-gradient-to-r ${color} text-white shadow-md`} data-testid="trial-mode-banner">
        <div className="px-3 sm:px-4 py-2 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm font-semibold flex-1 min-w-0">{msg}</p>
          <a href={`/pay/${state.school_id || 'default'}`} className="px-3 py-1.5 bg-white/95 hover:bg-white text-slate-900 rounded-full font-bold text-xs sm:text-sm whitespace-nowrap shadow-sm" data-testid="trial-banner-subscribe-btn">
            💳 {t('अभी Subscribe करें', 'Subscribe Now')}
          </a>
        </div>
      </div>
    );
  }

  let msg, color;
  const trialOn = !!state.trial_enabled;
  const payUrl = `/pay/${state.school_id || 'default'}`;

  // Payment-first (default): trial NOT enabled for this school → Guided Demo + pay.
  if (!trialOn) {
    const pmsg = expired
      ? t('आपका explore समय पूरा हो गया — पूरा setup चालू करने के लिए अभी pay करें।',
          'Your explore period ended — pay now to activate the full setup.')
      : t(`🎓 आप Guided Demo (sample data) देख रहे हैं${days != null ? ` · ${days} दिन` : ''} · पूरा setup (असली data, WhatsApp, Fees) payment के बाद चालू होगा।`,
          `🎓 You're viewing a Guided Demo (sample data)${days != null ? ` · ${days} days` : ''} · Full setup (real data, WhatsApp, Fees) activates after payment.`);
    const pcolor = expired ? 'from-rose-600 to-red-500' : 'from-orange-600 to-amber-500';
    return (
      <div className={`bg-gradient-to-r ${pcolor} text-white shadow-md`} data-testid="demo-mode-banner">
        <div className="px-3 sm:px-4 py-2 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm font-semibold flex-1 min-w-0">{pmsg}</p>
          <a href={payUrl} className="px-3 py-1.5 bg-white/95 hover:bg-white text-slate-900 rounded-full font-bold text-xs sm:text-sm whitespace-nowrap shadow-sm" data-testid="demo-banner-pay-btn">
            💳 {t('अभी Pay करके चालू करें', 'Pay & Activate')}
          </a>
        </div>
      </div>
    );
  }

  if (expired) {
    color = 'from-rose-600 to-red-500';
    msg = t('आपका explore समय पूरा हो गया — अपना data भरकर अभी "Activate My Trial" दबाएँ (Free 14 दिन)।',
            'Your explore period ended — enter your data and click "Activate My Trial" (Free 14 days).');
  } else if (days != null && days <= 3) {
    color = 'from-amber-600 to-orange-500';
    msg = t(`⏰ Explore के सिर्फ ${days} दिन बचे — अपना data भरकर "Activate My Trial" दबाएँ, तभी आपके Free 14 दिन शुरू होंगे।`,
            `⏰ Only ${days} explore day(s) left — enter your data and click "Activate My Trial" to start your Free 14 days.`);
  } else {
    color = 'from-blue-600 to-indigo-500';
    msg = t(`🎓 आप अभी portal explore कर रहे हैं (sample data)${days != null ? ` · ${days} दिन` : ''} · अपना असली data भरकर "Activate My Trial" दबाएँ — तभी आपके Free 14 दिन शुरू होंगे। (कोई payment नहीं)`,
            `🎓 You're exploring with sample data${days != null ? ` · ${days} days` : ''} · Enter your real data and click "Activate My Trial" — your Free 14 days start then. (No payment)`);
  }

  return (
    <div className={`bg-gradient-to-r ${color} text-white shadow-md`} data-testid="demo-mode-banner">
      <div className="px-3 sm:px-4 py-2 flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm font-semibold flex-1 min-w-0">{msg}</p>
        <button
          onClick={() => openActivateModal()}
          className="px-3 py-1.5 bg-white/95 hover:bg-white text-slate-900 rounded-full font-bold text-xs sm:text-sm whitespace-nowrap shadow-sm"
          data-testid="demo-banner-activate-btn"
        >
          🚀 {t('Activate My Trial (Free)', 'Activate My Trial (Free)')}
        </button>
      </div>
    </div>
  );
}

// ---------------- Suspended "Pay to reactivate" Gate ----------------

export function AccountSuspendedGate() {
  const { state } = useDemoMode();
  const { language } = useLanguage();
  if (!state || state.account_state !== 'suspended') return null;
  const t = (hi, en) => (language === 'hi' ? hi : en);
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/80 backdrop-blur-sm p-4" data-testid="account-suspended-gate">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden text-center">
        <div className="bg-gradient-to-r from-rose-600 to-red-600 text-white p-6">
          <div className="text-4xl mb-2">🔒</div>
          <h3 className="text-xl font-bold">{t('Account Suspended', 'Account Suspended')}</h3>
        </div>
        <div className="p-6 space-y-3">
          <p className="text-sm text-slate-600">
            {t('आपका free trial खत्म हो गया और payment pending है। आपका पूरा data सुरक्षित है — subscribe करते ही account तुरंत चालू हो जाएगा।',
               'Your free trial ended and payment is pending. Your data is safe — subscribe to reactivate instantly.')}
          </p>
          <a href={`/pay/${state.school_id || 'default'}`} className="block w-full py-3 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 hover:to-orange-600 text-white font-bold transition" data-testid="suspended-subscribe-btn">
            💳 {t('अभी Subscribe करके चालू करें', 'Subscribe & Reactivate Now')}
          </a>
          <p className="text-[11px] text-slate-400">{t('मदद चाहिए? info@shiksha-mitra.com', 'Need help? info@shiksha-mitra.com')}</p>
        </div>
      </div>
    </div>
  );
}

// ---------------- Floating Activate Pill ----------------

export function DemoFloatingActivate() {
  const { state, openActivateModal } = useDemoMode();
  const { language } = useLanguage();
  if (!state || !state.is_demo) return null;
  const t = (hi, en) => (language === 'hi' ? hi : en);
  // Payment-first (trial disabled) → floating pill goes to the pay page.
  if (!state.trial_enabled) {
    return (
      <a
        href={`/pay/${state.school_id || 'default'}`}
        className="fixed bottom-4 right-4 z-40 px-4 py-2.5 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 hover:to-orange-600 text-white font-bold shadow-xl shadow-orange-500/40 flex items-center gap-2"
        data-testid="demo-floating-pay"
      >
        <span>💳</span>
        <span className="text-sm">{t('अभी Pay करके Setup चालू करें', 'Pay & Activate Setup')}</span>
      </a>
    );
  }
  return (
    <button
      onClick={() => openActivateModal()}
      className="fixed bottom-4 right-4 z-40 px-4 py-2.5 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:to-emerald-700 text-white font-bold shadow-xl shadow-emerald-500/40 flex items-center gap-2 animate-pulse hover:animate-none"
      data-testid="demo-floating-activate"
    >
      <span>🚀</span>
      <span className="text-sm">{t('Activate My Trial (Free)', 'Activate My Trial (Free)')}</span>
    </button>
  );
}

// ---------------- Activate Modal ----------------

export function DemoActivateModal() {
  const { activateModalOpen, closeActivateModal, blockPayload, state, refresh } = useDemoMode();
  const { language } = useLanguage();
  const [udise, setUdise] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);
  if (!activateModalOpen) return null;
  const t = (hi, en) => (language === 'hi' ? hi : en);
  const expired = state?.expired || blockPayload?.expired;

  // Payment-first (trial disabled for this school) → show pay CTA, not trial start.
  if (state && !state.trial_enabled) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={closeActivateModal} data-testid="demo-activate-modal">
        <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden text-center" onClick={(e) => e.stopPropagation()}>
          <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white p-6">
            <div className="text-4xl mb-2">🚀</div>
            <h3 className="text-xl font-bold">{t('पूरा Setup चालू करें', 'Activate Full Setup')}</h3>
          </div>
          <div className="p-6 space-y-3">
            <p className="text-sm text-slate-600">
              {t('यह एक Guided Demo है। आपका असली data, WhatsApp, Admissions और Fees — सब कुछ payment के बाद चालू होगा।',
                 'This is a Guided Demo. Your real data, WhatsApp, Admissions and Fees all activate after payment.')}
            </p>
            <a href={`/pay/${state?.school_id || 'default'}`} className="block w-full py-3 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 hover:to-orange-600 text-white font-bold transition" data-testid="demo-paynow-btn">
              💳 {t('अभी Pay करके Setup चालू करें', 'Pay & Activate Setup')}
            </a>
            <button onClick={closeActivateModal} className="w-full py-2 text-slate-500 text-sm" data-testid="demo-paynow-cancel">
              {t('बाद में', 'Maybe later')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const finish = (activated) => {
    window.dispatchEvent(new Event(activated ? 'demo:activated' : 'demo:activate-cancelled'));
    setError(''); setUdise(''); setDone(null);
    closeActivateModal();
  };

  const handleCancel = () => finish(false);

  const startTrial = async () => {
    setError('');
    const code = udise.trim();
    if (code && !/^\d{11}$/.test(code)) {
      setError(t('UDISE+ code ठीक 11 अंकों का होना चाहिए।', 'UDISE+ code must be exactly 11 digits.'));
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/activation/start-trial`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          udise_code: code || null,
          manual: !code,
          school_name: state?.school_name || 'My School',
        }),
      });
      const data = await res.json();
      if (!res.ok || data.success === false) {
        setError(_errText(data && data.detail) || t('Trial शुरू नहीं हो सका।', 'Could not start trial.'));
        setLoading(false);
        return;
      }
      // Mark local user as no-longer-demo so client gating relaxes.
      try {
        const u = JSON.parse(localStorage.getItem('user') || 'null');
        if (u) { u.is_demo = false; u.account_state = 'trial'; localStorage.setItem('user', JSON.stringify(u)); }
      } catch { /* */ }
      setLoading(false);
      if (data.udise_verified && (data.udise_imported_fields || 0) > 0) {
        // Show a quick import confirmation, then resume the saved action.
        setDone(data);
        if (refresh) refresh();
      } else {
        if (refresh) refresh();
        finish(true);
      }
    } catch {
      setError(t('कुछ गड़बड़ हुई। दोबारा कोशिश करें।', 'Something went wrong. Please try again.'));
      setLoading(false);
    }
  };

  // ---- Import-success confirmation (UDISE auto-filled) ----
  if (done) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="demo-activate-modal">
        <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden text-center">
          <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white p-6">
            <div className="text-4xl mb-2">🎉</div>
            <h3 className="text-xl font-bold">{t('UDISE+ से data import हो गया!', 'Imported from UDISE+!')}</h3>
          </div>
          <div className="p-6 space-y-3">
            <p className="text-sm text-slate-600">
              {t(`आपकी school की ${done.udise_imported_fields} details अपने-आप भर गईं। अब बस verify करके आगे बढ़ें — आपका Free 14-दिन Trial चालू हो गया है।`,
                 `${done.udise_imported_fields} school details were auto-filled. Just verify and continue — your Free 14-day trial is now active.`)}
            </p>
            <button onClick={() => finish(true)} className="w-full py-3 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 hover:to-orange-600 text-white font-bold transition" data-testid="demo-import-continue-btn">
              {t('Verify करें & आगे बढ़ें', 'Verify & Continue')} →
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={handleCancel} data-testid="demo-activate-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white p-5">
          <h3 className="text-xl font-bold">
            {expired
              ? t('🔒 Explore समय समाप्त', '🔒 Explore Period Ended')
              : t('🚀 अपना असली data भरें — Free Trial शुरू करें', '🚀 Enter your real data — Start Free Trial')}
          </h3>
          <p className="text-sm text-emerald-50 mt-1">
            {t('Agree करते ही आपका Free 14-दिन Trial शुरू हो जाएगा और जो data आप भर रहे थे वही save हो जाएगा। (कोई payment नहीं)',
               'On Agree, your Free 14-day trial starts and the data you were entering is saved. (No payment)')}
          </p>
        </div>

        <div className="p-5 space-y-4">
          {error && <div className="bg-rose-50 text-rose-700 text-sm px-4 py-2.5 rounded-lg border border-rose-100" data-testid="demo-activate-error">{error}</div>}

          {/* Prominent UDISE+ Import option */}
          <div className="rounded-xl border-2 border-indigo-200 bg-indigo-50/60 p-4" data-testid="demo-udise-import-box">
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-indigo-600 text-white text-sm">⚡</span>
              <h4 className="font-bold text-indigo-900 text-sm">{t('सबसे आसान — UDISE+ से auto-import', 'Easiest — Auto-import from UDISE+')}</h4>
              <span className="ml-auto text-[10px] font-bold px-2 py-0.5 bg-indigo-600 text-white rounded-full">{t('सुझाया गया', 'RECOMMENDED')}</span>
            </div>
            <p className="text-xs text-indigo-700 mb-2.5">
              {t('अपना 11-अंकों का UDISE+ code डालें — आपकी पूरी school profile (नाम, principal, students, district आदि) अपने-आप भर जाएगी। कुछ भी manually भरने की ज़रूरत नहीं।',
                 'Enter your 11-digit UDISE+ code — your full school profile (name, principal, students, district, etc.) auto-fills. No manual entry needed.')}
            </p>
            <input
              value={udise}
              onChange={(e) => setUdise(e.target.value.replace(/\D/g, '').slice(0, 11))}
              placeholder={t('जैसे 23260102260', 'e.g. 23260102260')}
              inputMode="numeric"
              className="w-full px-3 py-2.5 rounded-lg border border-indigo-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none text-base tracking-wide font-mono"
              data-testid="demo-udise-input"
            />
          </div>

          <div className="text-center text-xs text-slate-400 font-semibold">{t('— या —', '— or —')}</div>

          <div className="space-y-1.5 text-sm text-slate-700">
            <p className="font-semibold">{t('Trial शुरू होने पर:', 'When your trial starts:')}</p>
            <ul className="space-y-1 pl-5 list-disc text-xs">
              <li>{t('आपका असली data live हो जाएगा (sample data हट जाएगा)', 'Your real data goes live (sample data is cleared)')}</li>
              <li>{t('पूरे 14 दिन उसी पल से शुरू — पूरा access', 'Full 14 days start from that moment — full access')}</li>
              <li>{t('Attendance, Fees, Communication, सभी 31 AI Features', 'Attendance, Fees, Communication, all 31 AI Features')}</li>
            </ul>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
            <p className="text-xs text-slate-600">{t('Trial के दौरान या बाद में जब चाहें subscribe करें', 'Subscribe anytime during or after the trial')}</p>
            <p className="text-sm font-bold text-slate-800">{t('Software FREE · one-time + सालाना maintenance', 'Software FREE · one-time + yearly maintenance')}<span className="block text-xs font-normal text-emerald-600">{t('कीमत आपके कुल users (छात्र+शिक्षक+staff) के slab पर तय', 'Price by your total-users (students+teachers+staff) slab')}</span></p>
          </div>
        </div>

        <div className="p-4 border-t border-slate-100 flex gap-2">
          <button
            onClick={handleCancel}
            disabled={loading}
            className="flex-1 px-4 py-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold disabled:opacity-50"
            data-testid="demo-activate-cancel"
          >
            {expired ? t('बाद में', 'Maybe later') : t('Explore जारी रखें', 'Keep exploring')}
          </button>
          <button
            onClick={startTrial}
            disabled={loading}
            className="flex-1 px-4 py-2.5 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-600 hover:to-emerald-700 text-white font-bold disabled:opacity-50"
            data-testid="demo-activate-pay-btn"
          >
            {loading ? t('शुरू हो रहा है…', 'Starting…') : `🚀 ${udise.trim() ? t('Import & Trial शुरू करें', 'Import & Start Trial') : t('Agree & Trial शुरू करें', 'Agree & Start Trial')}`}
          </button>
        </div>
      </div>
    </div>
  );
}
